import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { HitLoadingBlock } from "@/components/loading/Blocks";
import { CONDITIONS } from "@/lib/conditions";
import { uploadPublic, errorText } from "@/lib/publishQueue";
import { Mono, Field, Chip, Primary, PhotoTiles, BackPill, imgUrl, money, decimal } from "./ui";
import { useWideLayout } from "@/hooks/publish/useWideLayout";
import { DeskShell } from "./Desk";

const FILTERS = [
  ["all", "Todas", () => true],
  ["rev", "En revisión", (s) => s === "EN_REVISION"],
  ["falta", "Falta dato", (s) => s === "FALTA_DATO"],
  ["apr", "Aprobadas", (s) => s === "APROBADA_SIN_PUBLICAR" || s === "APROBADA_FALTA_TU_DATO"],
  ["pub", "Publicadas", (s) => s === "PUBLICADA"],
  ["rej", "Rechazadas", (s) => s === "RECHAZADA"],
];

const P7Desk = ({ items, reload, onPublish, onView, onPayout }) => {
  const [f, setF] = useState("all");
  const list = items || [];
  const fn = FILTERS.find((x) => x[0] === f)[2];
  return (
    <DeskShell testid="p7-solicitudes" micro="Nada queda sin publicar" a="Tus" b="solicitudes" left={
      items === null ? <HitLoadingBlock section="inventory" /> : (
        <div className="space-y-6">
          <div className="flex flex-wrap gap-2" data-testid="p7-filters">{FILTERS.map(([k, l, t]) => (
            <button key={k} type="button" onClick={() => setF(k)} data-testid={`p7-filter-${k}`} className="h-11 px-4 rounded-full border text-[14px]"
              style={f === k ? { borderColor: "#FF4438", background: "rgba(255,68,56,.12)", color: "#fff", fontWeight: 700 } : { borderColor: "rgba(255,255,255,.12)", color: "rgba(255,255,255,.75)" }}>
              {l} · {list.filter((it) => t(it.ui_status)).length}</button>))}
          </div>
          {list.length === 0 ? <div className="text-[14px] text-white/50" data-testid="p7-empty">Aún no tienes solicitudes. Cuando envíes una carta o producto nuevo, aparecerá aquí con su estado.</div>
            : <ul className="grid grid-cols-2 xl:grid-cols-3 gap-4 items-start" data-testid="p7-list">{list.filter((it) => fn(it.ui_status)).map((it) => <Req key={it.id} it={it} onPublish={onPublish} onView={onView} onPayout={onPayout} reload={reload} wide />)}</ul>}
        </div>
      )} />
  );
};

const ago = (iso) => {
  const h = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 3600000));
  return h < 1 ? "Enviada hace un momento." : h < 48 ? `Enviada hace ${h} h.` : `Enviada hace ${Math.round(h / 24)} días.`;
};

const ST = {
  EN_REVISION: { label: "EN REVISIÓN", c: "#fbbf24", seg: 2, text: "Al aprobarla se publica con tus datos y te avisamos." },
  PUBLICADA: { label: "PUBLICADA", c: "#34D399", seg: 3, text: "Aprobada y publicada automáticamente." },
  FALTA_DATO: { label: "FALTA 1 DATO", c: "#FF6B5E", seg: 2 },
  APROBADA_FALTA_TU_DATO: { label: "APROBADA · FALTA TU DATO", c: "#FF6B5E", seg: 3 },
  APROBADA_SIN_PUBLICAR: { label: "APROBADA", c: "#FFFFFF", seg: 3, text: "Ya está en el catálogo. Completa precio y fotos." },
  RECHAZADA: { label: "RECHAZADA", c: "#EF4444", seg: 2 },
};
const FIX = { photos: "Cambiar foto", price: "Corregir precio", name: "Corregir nombre", set_number: "Corregir set / número", condition: "Cambiar condición" };

const Fix = ({ it, onDone }) => {
  const field = it.needs_info?.field;
  const [v, setV] = useState({ price: it.pub_draft?.price || "", name: it.name_es || "", set_name: it.set_name || "", number: it.number || "", condition: it.pub_draft?.condition || "NM" });
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const body = { field };
      if (field === "photos") body.photo_paths = await Promise.all(photos.map(uploadPublic));
      if (field === "price") body.price = parseFloat(v.price);
      if (field === "name") body.name = v.name;
      if (field === "set_number") { body.set_name = v.set_name; body.number = v.number; }
      if (field === "condition") body.condition = v.condition;
      await api.patch(`/custom-cards/${it.id}/fix`, body);
      toast.success("Corregido · vuelve a revisión");
      onDone();
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 space-y-2" data-testid={`p7-fix-form-${it.id}`}>
      {field === "photos" && <PhotoTiles files={photos} onAdd={(fs) => setPhotos((p) => [...p, ...fs].slice(0, 10))} onRemove={(i) => setPhotos((p) => p.filter((_, j) => j !== i))} testid={`p7-fix-photos-${it.id}`} />}
      {field === "price" && <Field type="text" inputMode="decimal" value={v.price} onChange={(e) => setV({ ...v, price: decimal(e.target.value) })} data-testid={`p7-fix-price-${it.id}`} />}
      {field === "name" && <Field value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} data-testid={`p7-fix-name-${it.id}`} />}
      {field === "set_number" && <div className="grid grid-cols-2 gap-2"><Field value={v.set_name} onChange={(e) => setV({ ...v, set_name: e.target.value })} placeholder="Set" /><Field value={v.number} onChange={(e) => setV({ ...v, number: e.target.value })} placeholder="Nº" /></div>}
      {field === "condition" && <div className="grid grid-cols-5 gap-1.5">{CONDITIONS.map((c) => <Chip key={c.value} on={v.condition === c.value} onClick={() => setV({ ...v, condition: c.value })} className="sx-mono text-[11px]" data-testid={`p7-fix-cond-${it.id}-${c.value}`}>{c.value}</Chip>)}</div>}
      <Primary className="w-full h-10 text-[13px]" disabled={field === "photos" && photos.length < 2} loading={busy} loadingText="Enviando…" onClick={send} data-testid={`p7-fix-send-${it.id}`}>Enviar corrección</Primary>
    </div>
  );
};

const Req = ({ it, onPublish, onView, onPayout, reload, wide }) => {
  const [fixing, setFixing] = useState(false);
  const st = ST[it.ui_status] || ST.EN_REVISION;
  const img = imgUrl(it.image_url) || imgUrl(it.identification_photos?.[0]);
  const text = it.ui_status === "FALTA_DATO" ? it.needs_info?.reason : it.ui_status === "APROBADA_FALTA_TU_DATO" ? it.auto_publish_seller_issue
    : it.ui_status === "RECHAZADA" ? (it.reject_reason ? `Motivo: ${it.reject_reason}` : "Rechazada") : st.text;
  const cta = it.ui_status === "PUBLICADA" ? ["Ver", () => onView(it), "#34D399"] : it.ui_status === "APROBADA_SIN_PUBLICAR" ? ["Publicar", () => onPublish(it), "#FFFFFF"]
    : it.ui_status === "APROBADA_FALTA_TU_DATO" ? ["Agregar datos de cobro", onPayout, "#FF4438"] : it.ui_status === "FALTA_DATO" && !fixing ? [FIX[it.needs_info?.field] || "Corregir", () => setFixing(true), "#FF4438"] : null;
  const meta = [String(it.pub_draft?.language || it.language || "").toUpperCase(), it.pub_draft?.condition, it.pub_draft?.price && money(it.pub_draft.price)].filter(Boolean).join(" · ");
  return (
    <li className={`${wide ? "p-4 rounded-[20px]" : "p-3 rounded-[16px]"} border bg-[#101010]`} style={{ borderColor: wide && it.ui_status === "FALTA_DATO" ? "#FF4438" : `${st.c}55` }} data-testid={`p7-request-${it.id}`}>
      <div className="flex gap-3">
        <div className="w-10 h-14 rounded overflow-hidden bg-black shrink-0">{img && <img src={img} alt="" className="w-full h-full object-contain" />}</div>
        <div className="flex-1 min-w-0">
          <div className="text-[15px] font-bold text-white truncate">{it.name_es}{it.number ? ` · #${it.number}` : ""}</div>
          <div className="sx-mono text-[9.5px] text-white/45 uppercase">{meta}</div>
        </div>
        <span className="h-6 px-2 rounded-full border sx-mono text-[8.5px] grid place-items-center shrink-0" style={{ color: st.c, borderColor: st.c }} data-testid={`p7-status-${it.id}`}>{st.label}</span>
      </div>
      <div className="flex gap-1 mt-2.5">{[1, 2, 3].map((i) => <div key={i} className="h-[3px] flex-1 rounded-full" style={{ background: i <= st.seg ? st.c : "rgba(255,255,255,.1)" }} />)}</div>
      <div className="flex items-center justify-between gap-3 mt-2.5">
        {text && <div className="text-[12.5px] text-white/70" data-testid={`p7-text-${it.id}`}>{text}{wide && it.ui_status === "EN_REVISION" && it.created_at ? ` ${ago(it.created_at)}` : ""}</div>}
        {cta && <button type="button" onClick={cta[1]} className="h-9 px-4 rounded-full text-[13px] font-bold text-[#080808] shrink-0" style={{ background: cta[2] }} data-testid={`p7-cta-${it.id}`}>{cta[0]}</button>}
      </div>
      {fixing && <Fix it={it} onDone={() => { setFixing(false); reload(); }} />}
    </li>
  );
};

// P7 · Tus solicitudes · nada queda sin publicar
export const P7Solicitudes = (props) => {
  const wide = useWideLayout("(min-width: 1024px)");
  return wide ? <P7Desk {...props} /> : <P7Mobile {...props} />;
};

const P7Mobile = ({ items, reload, onPublish, onView, onPayout, onBack }) => (
  <div className="space-y-4" data-testid="p7-solicitudes">
    <BackPill onClick={onBack} data-testid="p7-back">← Volver</BackPill>
    <Mono tone="#FF6B5E">Nada queda sin publicar</Mono>
    <h1 className="font-extrabold tracking-[-.05em] leading-[.92] text-[36px] text-white">Tus <span className="italic font-normal text-white/60">solicitudes</span></h1>
    {items === null ? <HitLoadingBlock section="inventory" /> : items.length === 0
      ? <div className="text-[13px] text-white/50" data-testid="p7-empty">Aún no tienes solicitudes. Cuando envíes una carta o producto nuevo, aparecerá aquí con su estado.</div>
      : <ul className="space-y-2.5" data-testid="p7-list">{items.map((it) => <Req key={it.id} it={it} onPublish={onPublish} onView={onView} onPayout={onPayout} reload={reload} />)}</ul>}
  </div>
);
